/* TEFCO — collection filtering/sorting via the Section Rendering API.
   Progressive enhancement: the GET form + filter/pagination links already work
   with JS disabled; this intercepts them and swaps [data-tefco-target] in place. */
(function () {
  if (customElements.get('tefco-collection')) return;

  customElements.define('tefco-collection', class extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.id;
      this.baseUrl = this.dataset.url;
      this.wrapper = this.closest('.shopify-section') || this;
      this.debouncedApply = this.debounce(() => this.apply(), 450);

      this.onChange = this.onChange.bind(this);
      this.onClick = this.onClick.bind(this);
      this.onPop = this.onPop.bind(this);

      this.addEventListener('change', this.onChange);
      this.addEventListener('click', this.onClick);
      window.addEventListener('popstate', this.onPop);

      this.initSliders();
    }

    disconnectedCallback() {
      window.removeEventListener('popstate', this.onPop);
    }

    get form() { return this.querySelector('[data-tefco-facets]'); }

    onChange(e) {
      const t = e.target;
      if (t.matches('[data-tefco-sort]')) return this.apply();
      if (t.matches('input[type="checkbox"][name^="filter."]')) return this.apply();
      if (t.matches('[data-price-min], [data-price-max]')) return this.debouncedApply();
    }

    onClick(e) {
      const a = e.target.closest('a[data-tefco-ajax]');
      if (a) { e.preventDefault(); this.render(a.href, true, true); }
    }

    onPop() { this.render(location.href, false, false); }

    buildUrl() {
      const params = new URLSearchParams();
      if (this.form) {
        new FormData(this.form).forEach((v, k) => {
          if (v !== '' && v != null) params.append(k, v);
        });
      }
      const qs = params.toString();
      return this.baseUrl + (qs ? '?' + qs : '');
    }

    apply() { this.render(this.buildUrl(), true, false); }

    async render(url, push, scroll) {
      const section = this.wrapper;
      section.classList.add('is-loading');
      // The dimmed grid is a visual-only cue. aria-busy tells a screen reader the
      // region is mid-update so it does not announce half-swapped results.
      section.setAttribute('aria-busy', 'true');
      try {
        const u = new URL(url, location.origin);
        if (push) history.pushState({ tefco: true }, '', u.toString());
        u.searchParams.set('section_id', this.sectionId);

        const res = await fetch(u.toString());
        if (!res.ok) throw new Error('HTTP ' + res.status);
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const next = doc.querySelector('[data-tefco-target]');
        const cur = this.querySelector('[data-tefco-target]');
        if (next && cur) {
          cur.innerHTML = next.innerHTML;
          this.initSliders();
          if (scroll) {
            const top = this.querySelector('.tefco-cp-results');
            if (top) top.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }
      } catch (err) {
        console.error('TEFCO collection filter error:', err);
        // fall back to a full navigation so the user is never stuck
        if (push) window.location = url;
      } finally {
        section.classList.remove('is-loading');
        section.removeAttribute('aria-busy');
      }
    }

    /* ---------------- price sliders ---------------- */
    initSliders() {
      this.querySelectorAll('[data-tefco-price]').forEach((el) => this.wireSlider(el));
    }

    wireSlider(el) {
      if (el.dataset.wired) return;
      const max = parseFloat(el.dataset.max) || 0;
      const track = el.querySelector('.tefco-cp-price__track');
      const fill = el.querySelector('.tefco-cp-price__fill');
      const thumbMin = el.querySelector('[data-thumb="min"]');
      const thumbMax = el.querySelector('[data-thumb="max"]');
      const inputMin = el.querySelector('[data-price-min]');
      const inputMax = el.querySelector('[data-price-max]');
      if (!track || !fill || !thumbMin || !thumbMax || !inputMin || !inputMax || !max) return;
      el.dataset.wired = '1';

      const state = {
        min: parseFloat(inputMin.value) || 0,
        max: parseFloat(inputMax.value) || max
      };
      /* RTL (§7): the slider is the one control whose geometry the theme drives
         from JS, so logical CSS properties alone cannot fix it. Positions are
         written with inset-inline-*, and the drag maths measures from the
         inline-start edge — which is rect.right in RTL, not rect.left. */
      const isRtl = () =>
        (document.documentElement.getAttribute('dir') || '').toLowerCase() === 'rtl' ||
        getComputedStyle(el).direction === 'rtl';

      const paint = () => {
        const a = Math.min(state.min, state.max);
        const b = Math.max(state.min, state.max);
        const pa = (a / max) * 100;
        const pb = (b / max) * 100;
        fill.style.insetInlineStart = pa + '%';
        fill.style.insetInlineEnd = (100 - pb) + '%';
        thumbMin.style.insetInlineStart = pa + '%';
        thumbMax.style.insetInlineStart = pb + '%';
        thumbMin.setAttribute('aria-valuenow', Math.round(state.min));
        thumbMax.setAttribute('aria-valuenow', Math.round(state.max));
      };
      paint();

      const startDrag = (key) => (ev) => {
        ev.preventDefault();
        const rect = track.getBoundingClientRect();
        const rtl = isRtl();
        const move = (e) => {
          const clientX = e.touches ? e.touches[0].clientX : e.clientX;
          // Distance from the inline-start edge, whichever side that is.
          const fromStart = rtl ? rect.right - clientX : clientX - rect.left;
          const pct = Math.max(0, Math.min(1, fromStart / rect.width));
          const val = Math.round(pct * max);
          state[key] = val;
          if (key === 'min') inputMin.value = Math.min(val, Math.round(state.max));
          else inputMax.value = Math.max(val, Math.round(state.min));
          paint();
        };
        const up = () => {
          document.removeEventListener('pointermove', move);
          document.removeEventListener('pointerup', up);
          this.debouncedApply();
        };
        document.addEventListener('pointermove', move);
        document.addEventListener('pointerup', up);
      };
      thumbMin.addEventListener('pointerdown', startDrag('min'));
      thumbMax.addEventListener('pointerdown', startDrag('max'));

      const step = Math.max(1, Math.ceil(max / 100));
      const onKey = (key) => (e) => {
        let d = 0;
        // Horizontal arrows mirror in RTL — matching native <input type="range">.
        // Vertical arrows never mirror, so Up is always increase.
        const sign = isRtl() ? -1 : 1;
        if (e.key === 'ArrowRight') d = step * sign;
        else if (e.key === 'ArrowLeft') d = -step * sign;
        else if (e.key === 'ArrowUp') d = step;
        else if (e.key === 'ArrowDown') d = -step;
        if (!d) return;
        e.preventDefault();
        state[key] = Math.max(0, Math.min(max, (state[key] || 0) + d));
        if (key === 'min') inputMin.value = Math.min(Math.round(state.min), Math.round(state.max));
        else inputMax.value = Math.max(Math.round(state.max), Math.round(state.min));
        paint();
        this.debouncedApply();
      };
      thumbMin.addEventListener('keydown', onKey('min'));
      thumbMax.addEventListener('keydown', onKey('max'));

      // typing in the number inputs keeps the thumbs in sync
      inputMin.addEventListener('input', () => { state.min = parseFloat(inputMin.value) || 0; paint(); });
      inputMax.addEventListener('input', () => { state.max = parseFloat(inputMax.value) || max; paint(); });
    }

    debounce(fn, wait) {
      let t;
      return (...args) => { clearTimeout(t); t = setTimeout(() => fn.apply(this, args), wait); };
    }
  });
})();
